import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  EmailAuthProvider,
  reauthenticateWithCredential,
  multiFactor
} from 'firebase/auth';

export const MfaDisable = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Reauthentication state
  const [reauthEmail, setReauthEmail] = useState("");
  const [reauthPassword, setReauthPassword] = useState("");
  const [showReauthDialog, setShowReauthDialog] = useState(true);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Handle reauthentication and MFA disabling
  const handleDisableMfa = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, reauthenticate the user
      const credential = EmailAuthProvider.credential(reauthEmail, reauthPassword);
      await reauthenticateWithCredential(currentUser, credential);
      
      // Get the MFA user
      const multiFactorUser = multiFactor(currentUser);
      
      // Unenroll all factors
      const enrolledFactors = multiFactorUser.enrolledFactors;
      if (enrolledFactors.length > 0) {
        await multiFactorUser.unenroll(enrolledFactors[0]);
      }
      
      // Refresh the user object
      await currentUser.reload();
      
      setSuccess("Two-factor authentication has been successfully disabled");
      setShowReauthDialog(false);
      
      // Navigate back to settings after a short delay
      setTimeout(() => {
        navigate('/settings', { 
          state: { 
            message: "Two-factor authentication has been successfully disabled"
          }
        });
      }, 1500);
      
    } catch (error) {
      console.error("Error disabling MFA:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Disable Two-Factor Authentication
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Reauthentication Dialog */}
        <Dialog 
          open={showReauthDialog} 
          onClose={() => !loading && navigate('/settings')}
        >
          <DialogTitle>Confirm Disable 2FA</DialogTitle>
          <DialogContent>
            <DialogContentText>
              For security reasons, please re-enter your credentials to disable two-factor authentication.
              This will make your account less secure.
            </DialogContentText>
            <TextField
              margin="normal"
              required
              fullWidth
              id="reauthEmail"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={reauthEmail}
              onChange={(e) => setReauthEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="reauthPassword"
              autoComplete="current-password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              disabled={loading}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => navigate('/settings')} 
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDisableMfa}
              disabled={loading || !reauthEmail || !reauthPassword}
              variant="contained"
              color="error"
            >
              {loading ? <CircularProgress size={24} /> : 'Disable 2FA'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}; 